import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Contact, ContactInput } from 'zitimail-shared-types';

import { contacts } from '../api/calendar';
import {
  CloseIcon,
  PenIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  UsersIcon,
  BackIcon,
} from '../components/icons';
import { useToast } from '../components/Toasts';
import { hue, initials } from '../mail/format';

const EMPTY: ContactInput = { name: '', emails: [], phones: [] };
const lines = (s: string) =>
  s
    .split(/[\n,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);

function Avatar({
  name,
  email,
  large,
}: {
  name: string;
  email: string;
  large?: boolean;
}): JSX.Element {
  return (
    <span
      className={large ? 'avatar-lg avatar-xl' : 'avatar-lg'}
      style={{ ['--av-h' as string]: hue(email || name) }}
      aria-hidden="true"
    >
      {initials({ name, address: email || name })}
    </span>
  );
}

/** The user's address book (CardDAV), with the organisation's directory alongside. */
export function ContactsView({ onWrite }: { onWrite: (address: string) => void }): JSX.Element {
  const toast = useToast();
  const [list, setList] = useState<Contact[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Contact | null>(null);
  const [editing, setEditing] = useState<ContactInput | null>(null);
  const [colleagues, setColleagues] = useState<Array<{ name: string; email: string }>>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    contacts
      .list()
      .then((all) => {
        setList(all);
        setSelected((current) =>
          current ? (all.find((c) => c.uid === current.uid) ?? null) : null,
        );
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load contacts.'))
      .finally(() => setLoaded(true));
  }, []);
  useEffect(load, [load]);

  // Colleagues from the organisation's directory, for the same search.
  useEffect(() => {
    const term = query.trim();
    if (!term) {
      setColleagues([]);
      return;
    }
    const timer = window.setTimeout(() => {
      contacts
        .directory(term)
        .then(setColleagues)
        .catch(() => setColleagues([]));
    }, 200);
    return () => window.clearTimeout(timer);
  }, [query]);

  const q = query.trim().toLowerCase();
  const shown = q
    ? list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) || c.emails.some((e) => e.toLowerCase().includes(q)),
      )
    : list;
  const groups = useMemo(() => {
    const map = new Map<string, Contact[]>();
    for (const c of shown) {
      const letter = /^[a-z]/i.test(c.name) ? (c.name[0]?.toUpperCase() ?? '#') : '#';
      map.set(letter, [...(map.get(letter) ?? []), c]);
    }
    return [...map.entries()].sort(([a], [b]) =>
      a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b),
    );
  }, [shown]);
  const known = (email: string) =>
    list.some((c) => c.emails.some((e) => e.toLowerCase() === email.toLowerCase()));

  const save = async () => {
    if (!editing) {
      return;
    }
    try {
      if (selected) {
        await contacts.update(selected.href, selected.etag, editing);
        toast('Contact saved.');
      } else {
        await contacts.create(editing);
        toast('Contact added.');
      }
      setEditing(null);
      setError(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Not saved.');
    }
  };

  return (
    <main className={`main contacts-layout${selected || editing ? ' showing' : ''}`}>
      <section className="contacts-list" aria-label="Contacts">
        <div className="list-head">
          <label className="search-field">
            <SearchIcon />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search contacts and your organisation"
              aria-label="Search contacts"
            />
          </label>
          <button
            type="button"
            className="icon-btn"
            title="New contact"
            aria-label="New contact"
            onClick={() => {
              setSelected(null);
              setEditing({ ...EMPTY });
            }}
          >
            <PlusIcon />
          </button>
        </div>
        {loaded && shown.length === 0 && colleagues.length === 0 && (
          <div className="list-empty">
            {q ? (
              <p>Nobody matches “{query}”.</p>
            ) : (
              <p>No contacts yet. Add one, or save a colleague from a search.</p>
            )}
          </div>
        )}
        {groups.map(([letter, people]) => (
          <section key={letter} aria-label={letter}>
            <h4 className="letter">{letter}</h4>
            <ul className="people">
              {people.map((c) => (
                <li key={c.href}>
                  <button
                    type="button"
                    className={`person${selected?.uid === c.uid ? ' on' : ''}`}
                    onClick={() => {
                      setSelected(c);
                      setEditing(null);
                    }}
                  >
                    <Avatar name={c.name} email={c.emails[0] ?? ''} />
                    <span className="person-name">{c.name}</span>
                    <span className="person-sub">
                      {c.organization ?? c.emails[0] ?? c.phones[0] ?? ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {colleagues.filter((p) => !known(p.email)).length > 0 && (
          <section aria-label="Your organisation">
            <h4 className="letter">
              <UsersIcon /> Your organisation
            </h4>
            <ul className="people">
              {colleagues
                .filter((p) => !known(p.email))
                .map((p) => (
                  <li key={p.email} className="person directory">
                    <Avatar name={p.name} email={p.email} />
                    <span className="person-name">{p.name}</span>
                    <span className="person-sub">{p.email}</span>
                    <span className="person-actions">
                      <button type="button" className="text-btn" onClick={() => onWrite(p.email)}>
                        Write
                      </button>
                      <button
                        type="button"
                        className="text-btn"
                        onClick={() =>
                          void contacts
                            .create({ name: p.name, emails: [p.email], phones: [] })
                            .then(() => {
                              toast(`${p.name} saved to your contacts.`);
                              load();
                            })
                            .catch((e: unknown) =>
                              toast(e instanceof Error ? e.message : 'Not saved.', {
                                tone: 'error',
                              }),
                            )
                        }
                      >
                        Save
                      </button>
                    </span>
                  </li>
                ))}
            </ul>
          </section>
        )}
      </section>

      <section className="contact-pane" aria-label="Contact">
        <button
          type="button"
          className="icon-btn mobile-only"
          title="Back"
          aria-label="Back to contacts"
          onClick={() => {
            setEditing(null);
            setSelected(null);
          }}
        >
          <BackIcon />
        </button>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        {editing ? (
          <form
            className="contact-card editing"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <div className="card-top">
              <Avatar name={editing.name || '?'} email={editing.emails[0] ?? ''} large />
              <h2>{selected ? 'Edit contact' : 'New contact'}</h2>
              <button
                type="button"
                className="icon-btn"
                aria-label="Cancel"
                onClick={() => setEditing(null)}
              >
                <CloseIcon />
              </button>
            </div>
            <label>
              Name
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                autoFocus
              />
            </label>
            <label>
              Organisation
              <input
                value={editing.organization ?? ''}
                onChange={(e) => setEditing({ ...editing, organization: e.target.value })}
              />
            </label>
            <label>
              Email addresses <span className="muted">— one per line</span>
              <textarea
                rows={2}
                value={editing.emails.join('\n')}
                onChange={(e) => setEditing({ ...editing, emails: lines(e.target.value) })}
              />
            </label>
            <label>
              Phone numbers <span className="muted">— one per line</span>
              <textarea
                rows={2}
                value={editing.phones.join('\n')}
                onChange={(e) => setEditing({ ...editing, phones: lines(e.target.value) })}
              />
            </label>
            <label>
              Notes
              <textarea
                rows={3}
                value={editing.note ?? ''}
                onChange={(e) => setEditing({ ...editing, note: e.target.value })}
              />
            </label>
            <div className="card-actions-row">
              <button type="submit" className="btn" style={{ width: 'auto' }}>
                {selected ? 'Save changes' : 'Add contact'}
              </button>
              <button
                type="button"
                className="btn ghost"
                style={{ width: 'auto' }}
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : selected ? (
          <article className="contact-card">
            <div className="card-top">
              <Avatar name={selected.name} email={selected.emails[0] ?? ''} large />
              <div>
                <h2>{selected.name}</h2>
                {selected.organization && <p className="muted">{selected.organization}</p>}
              </div>
            </div>
            <div className="card-actions-row">
              {selected.emails[0] && (
                <button
                  type="button"
                  className="btn"
                  style={{ width: 'auto' }}
                  onClick={() => onWrite(selected.emails[0] ?? '')}
                >
                  Write
                </button>
              )}
              <button
                type="button"
                className="btn ghost"
                style={{ width: 'auto' }}
                onClick={() =>
                  setEditing({
                    name: selected.name,
                    emails: selected.emails,
                    phones: selected.phones,
                    ...(selected.organization ? { organization: selected.organization } : {}),
                    ...(selected.note ? { note: selected.note } : {}),
                  })
                }
              >
                <PenIcon /> Edit
              </button>
              <button
                type="button"
                className="btn ghost delete-btn"
                style={{ width: 'auto' }}
                onClick={() =>
                  void contacts
                    .remove(selected.href, selected.etag)
                    .then(() => {
                      toast(`${selected.name} deleted.`);
                      setSelected(null);
                      load();
                    })
                    .catch((e: unknown) =>
                      toast(e instanceof Error ? e.message : 'Not deleted.', { tone: 'error' }),
                    )
                }
              >
                <TrashIcon /> Delete
              </button>
            </div>
            <dl className="contact-facts">
              {selected.emails.length > 0 && (
                <>
                  <dt>Email</dt>
                  <dd>
                    {selected.emails.map((e) => (
                      <button
                        key={e}
                        type="button"
                        className="link-btn addr"
                        onClick={() => onWrite(e)}
                      >
                        {e}
                      </button>
                    ))}
                  </dd>
                </>
              )}
              {selected.phones.length > 0 && (
                <>
                  <dt>Phone</dt>
                  <dd>
                    {selected.phones.map((p) => (
                      <a key={p} href={`tel:${p.replace(/[^\d+]/g, '')}`} className="addr">
                        {p}
                      </a>
                    ))}
                  </dd>
                </>
              )}
              {selected.note && (
                <>
                  <dt>Notes</dt>
                  <dd className="note">{selected.note}</dd>
                </>
              )}
            </dl>
          </article>
        ) : (
          <div className="empty">
            <div className="icon" aria-hidden="true">
              <UsersIcon />
            </div>
            <h3>No contact open</h3>
            <p className="muted">
              Choose someone, or search to find a colleague in your organisation.
            </p>
          </div>
        )}
      </section>
    </main>
  );
}
